// admin/src/pages/ManageUsers.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import adminApi from '../services/adminApi';
import './ManageUsers.css';

/* ── Helpers ─────────────────────────────────────────── */
const fmtDate     = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtDateTime = (d) => new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const fmtPrice    = (p) => (p || 0).toLocaleString('en-IN');
const timeAgo     = (date) => {
  const m = Math.floor((Date.now() - new Date(date)) / 60000);
  if (m < 1)  return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 7 ? `${d}d ago` : fmtDate(date);
};

const FILTER_CONFIG = {
  all:        { label: 'All Users'       },
  google:     { label: 'Google Sign-In'  },
  local:      { label: 'Email / Password'},
  active:     { label: 'Active'          },
  inactive:   { label: 'Inactive'        },
  incomplete: { label: 'Setup Pending'   },
};

/* ── Filter Dropdown ─────────────────────────────────── */
const FilterDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="mu-dd" ref={ref}>
      <button className="mu-dd__btn" onClick={() => setOpen(!open)} type="button">
        <span className="mu-dd__label">{FILTER_CONFIG[value]?.label || 'All Users'}</span>
        <svg className={`mu-dd__chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div className="mu-dd__menu">
          {Object.entries(FILTER_CONFIG).map(([key, cfg]) => (
            <button key={key} type="button"
              className={`mu-dd__item ${value === key ? 'active' : ''}`}
              onClick={() => { onChange(key); setOpen(false); }}>
              <span>{cfg.label}</span>
              {value === key && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Auth provider badge ─────────────────────────────── */
const ProviderBadge = ({ provider }) =>
  provider === 'google' ? (
    <span className="mu-provider mu-provider--google">
      <svg viewBox="0 0 24 24" width="11" height="11">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      Google
    </span>
  ) : (
    <span className="mu-provider mu-provider--local">
      <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
      Email
    </span>
  );

/* ── Status Badge ────────────────────────────────────── */
const StatusBadge = ({ isActive, profileComplete }) => {
  if (profileComplete === false) {
    return <span className="mu-status mu-status--pending">Setup Pending</span>;
  }
  return isActive
    ? <span className="mu-status mu-status--active">Active</span>
    : <span className="mu-status mu-status--inactive">Inactive</span>;
};

/* ── Booking status dot colours ─────────────────────── */
const BOOKING_STATUS_COLOR = {
  requested: '#7c3aed',
  pending:   '#d97706',
  confirmed: '#16a34a',
  cancelled: '#dc2626',
  completed: '#2563eb',
};

/* ── Stat Card ───────────────────────────────────────── */
const Stat = ({ value, label, accent, icon }) => (
  <div className="mu-stat" style={{ '--a': accent }}>
    <div className="mu-stat__icon">{icon}</div>
    <div className="mu-stat__info">
      <span className="mu-stat__val">{value}</span>
      <span className="mu-stat__lbl">{label}</span>
    </div>
    <div className="mu-stat__line" />
  </div>
);

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function ManageUsers() {
  const [users,     setUsers]     = useState([]);
  const [stats,     setStats]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [filter,    setFilter]    = useState('all');
  const [selected,  setSelected]  = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [toggling,  setToggling]  = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const searchTimerRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const d = await adminApi.getUserStats();
      if (d.success) setStats(d.stats);
    } catch {}
  }, []);

  const fetchUsers = useCallback(async (q = search, f = filter) => {
    try {
      setLoading(true); setError('');
      const d = await adminApi.getUsers({ search: q, filter: f });
      if (d.success) setUsers(d.users);
      else setError(d.message || 'Failed to load users.');
    } catch {
      setError('Cannot connect to server.');
    } finally {
      setLoading(false);
    }
  }, [search, filter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => fetchUsers(search, filter), 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [search, filter]); // eslint-disable-line

  const openModal = async (user) => {
    setSelected({ ...user, bookings: null });
    setShowModal(true);
    setConfirmDelete(false);
    setModalLoading(true);
    try {
      const d = await adminApi.getUserById(user._id);
      if (d.success) setSelected({ ...d.user, bookings: d.bookings });
    } catch {}
    finally { setModalLoading(false); }
  };

  const handleToggle = async () => {
    if (!selected) return;
    setToggling(true);
    try {
      const d = await adminApi.toggleUserStatus(selected._id);
      if (d.success) {
        const updated = { ...selected, isActive: d.isActive };
        setSelected(updated);
        setUsers(p => p.map(u => u._id === selected._id ? { ...u, isActive: d.isActive } : u));
        fetchStats();
      } else {
        alert(d.message || 'Failed to update status.');
      }
    } catch { alert('Failed to update. Please try again.'); }
    finally { setToggling(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      const d = await adminApi.deleteUser(selected._id);
      if (d.success) {
        setUsers(p => p.filter(u => u._id !== selected._id));
        setShowModal(false);
        fetchStats();
      } else {
        alert(d.message || 'Failed to delete user.');
      }
    } catch { alert('Failed to delete. Please try again.'); }
    finally { setDeleting(false); setConfirmDelete(false); }
  };

  const Avatar = ({ user, size = 36 }) => {
    const letter = (user.fullName || user.username || '?').charAt(0).toUpperCase();
    return user.avatar
      ? <img src={user.avatar} alt={user.fullName} className="mu-avatar mu-avatar--img" style={{ width: size, height: size }} />
      : <div className="mu-avatar" style={{ width: size, height: size, fontSize: size * 0.38 }}>{letter}</div>;
  };

  return (
    <div className="mu">

      {/* ── Header ── */}
      <div className="mu__header">
        <div className="mu__header-left">
          <h1 className="mu__title">Manage Users</h1>
          <p className="mu__sub">View and manage registered users</p>
        </div>
        <div className="mu__header-right">
          <div className="mu__search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input type="text" placeholder="Search name, email, username…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && (
              <button className="mu__search-clear" onClick={() => setSearch('')} type="button">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>
          <FilterDropdown value={filter} onChange={setFilter} />
          <button className="mu__refresh" onClick={() => { fetchUsers(); fetchStats(); }} title="Refresh">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      {stats && (
        <div className="mu__stats">
          <Stat value={stats.total}        label="Total Users"      accent="#1a1e18"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>}
          />
          <Stat value={stats.googleUsers}  label="Google Sign-In"   accent="#4285F4"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>}
          />
          <Stat value={stats.localUsers}   label="Email / Password" accent="#7c3aed"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>}
          />
          <Stat value={stats.activeUsers}  label="Active"           accent="#16a34a"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>}
          />
          <Stat value={stats.newThisMonth} label="New This Month"   accent="#d97706"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
          />
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="mu__error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span>{error}</span>
          <button onClick={() => fetchUsers()}>Retry</button>
        </div>
      )}

      {/* ── Table card ── */}
      <div className="mu__card">
        {loading ? (
          <div className="mu__loading"><div className="mu__spinner" /><p>Loading users…</p></div>
        ) : users.length === 0 ? (
          <div className="mu__empty">
            <div className="mu__empty-ico">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
              </svg>
            </div>
            <h3>No users found</h3>
            <p>{search ? `No results for "${search}".` : filter !== 'all' ? `No ${FILTER_CONFIG[filter]?.label.toLowerCase()} users yet.` : 'Users will appear here once they register.'}</p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="mu__mobile-list">
              {users.map(u => (
                <div key={u._id} className="mu__mobile-card" onClick={() => openModal(u)}>
                  <div className="mu__mobile-top">
                    <div className="mu__mobile-user">
                      <Avatar user={u} size={40} />
                      <div>
                        <div className="mu__mobile-name">{u.fullName}</div>
                        <div className="mu__mobile-username">@{u.username}</div>
                      </div>
                    </div>
                    <StatusBadge isActive={u.isActive} profileComplete={u.profileComplete} />
                  </div>
                  <div className="mu__mobile-email">{u.email}</div>
                  <div className="mu__mobile-meta">
                    <ProviderBadge provider={u.authProvider} />
                    <span>{u.bookingCount || 0} booking{u.bookingCount !== 1 ? 's' : ''}</span>
                    <span>Joined {timeAgo(u.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="mu__table-wrap">
              <table className="mu__table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Username</th>
                    <th>Provider</th>
                    <th>Bookings</th>
                    <th>Spent</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u._id} className="mu__row" onClick={() => openModal(u)}>
                      <td>
                        <div className="mu__user-cell">
                          <Avatar user={u} size={34} />
                          <span className="mu__fullname">{u.fullName}</span>
                        </div>
                      </td>
                      <td><span className="mu__email">{u.email}</span></td>
                      <td><span className="mu__username">@{u.username}</span></td>
                      <td><ProviderBadge provider={u.authProvider} /></td>
                      <td>
                        <span className="mu__bookings">
                          {u.bookingCount > 0
                            ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> {u.bookingCount}</>
                            : <span className="mu__no-bookings">—</span>
                          }
                        </span>
                      </td>
                      <td><span className="mu__spent">{u.bookingTotal > 0 ? `₹${fmtPrice(u.bookingTotal)}` : '—'}</span></td>
                      <td><StatusBadge isActive={u.isActive} profileComplete={u.profileComplete} /></td>
                      <td><span className="mu__time">{timeAgo(u.createdAt)}</span></td>
                      <td>
                        <button className="mu__view-btn" onClick={e => { e.stopPropagation(); openModal(u); }}>
                          View
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mu__footer">
              <span>{users.length} user{users.length !== 1 ? 's' : ''}{filter !== 'all' ? ` · ${FILTER_CONFIG[filter]?.label}` : ''}{search ? ` · "${search}"` : ''}</span>
            </div>
          </>
        )}
      </div>

      {/* ════════════════════════════════════════
          MODAL
      ════════════════════════════════════════ */}
      {showModal && selected && (
        <div className="mu__overlay" onClick={() => setShowModal(false)}>
          <div className="mu__modal" onClick={e => e.stopPropagation()}>

            <div className="mu__modal-hd">
              <div className="mu__modal-hd-left">
                <Avatar user={selected} size={44} />
                <div>
                  <h2 className="mu__modal-title">{selected.fullName}</h2>
                  <span className="mu__modal-sub">@{selected.username}</span>
                </div>
              </div>
              <button className="mu__modal-close" onClick={() => setShowModal(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {modalLoading ? (
              <div className="mu__modal-loading"><div className="mu__spinner" /><p>Loading user details…</p></div>
            ) : (
              <div className="mu__modal-body">

                {/* Action buttons */}
                <div className="mu__modal-actions">
                  <button className={`mu__act ${selected.isActive ? 'mu__act--deactivate' : 'mu__act--activate'}`} disabled={toggling} onClick={handleToggle}>
                    {toggling ? (
                      <><span className="mu__act-spinner" /> Updating…</>
                    ) : selected.isActive ? (
                      <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Deactivate</>
                    ) : (
                      <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Activate</>
                    )}
                  </button>

                  {!confirmDelete ? (
                    <button className="mu__act mu__act--delete" onClick={() => setConfirmDelete(true)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                      </svg>
                      Delete User
                    </button>
                  ) : (
                    <div className="mu__confirm-delete">
                      <span>Are you sure?</span>
                      <button className="mu__act mu__act--delete-confirm" onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Deleting…' : 'Yes, Delete'}
                      </button>
                      <button className="mu__act mu__act--cancel-del" onClick={() => setConfirmDelete(false)}>Cancel</button>
                    </div>
                  )}
                </div>

                {/* ── Profile section ── */}
                <div className="mu__section">
                  <h3 className="mu__section-title">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                    Profile
                  </h3>
                  <div className="mu__grid">
                    <div className="mu__field">
                      <span className="mu__lbl">Full Name</span>
                      <span className="mu__val">{selected.fullName}</span>
                    </div>
                    <div className="mu__field">
                      <span className="mu__lbl">Username</span>
                      <span className="mu__val">@{selected.username}</span>
                    </div>
                    <div className="mu__field mu__field--full">
                      <span className="mu__lbl">Email</span>
                      <a href={`mailto:${selected.email}`} className="mu__val mu__val--link">{selected.email}</a>
                    </div>
                    {selected.phone && (
                      <div className="mu__field">
                        <span className="mu__lbl">Phone</span>
                        <a href={`tel:${selected.phone}`} className="mu__val mu__val--link">{selected.phone}</a>
                      </div>
                    )}
                    {/* ★ City field — shows only if set */}
                    {selected.city && (
                      <div className="mu__field">
                        <span className="mu__lbl">City</span>
                        <span className="mu__val">{selected.city}</span>
                      </div>
                    )}
                    <div className="mu__field">
                      <span className="mu__lbl">Auth Provider</span>
                      <span className="mu__val"><ProviderBadge provider={selected.authProvider} /></span>
                    </div>
                    <div className="mu__field">
                      <span className="mu__lbl">Account Status</span>
                      <span className="mu__val">
                        <StatusBadge isActive={selected.isActive} profileComplete={selected.profileComplete} />
                      </span>
                    </div>
                    <div className="mu__field">
                      <span className="mu__lbl">Profile Setup</span>
                      <span className="mu__val">
                        {selected.profileComplete === false
                          ? <span style={{ color: '#d97706', fontWeight: 700 }}>Incomplete</span>
                          : <span style={{ color: '#16a34a', fontWeight: 700 }}>Complete</span>
                        }
                      </span>
                    </div>
                    <div className="mu__field">
                      <span className="mu__lbl">Joined</span>
                      <span className="mu__val">{fmtDate(selected.createdAt)}</span>
                    </div>
                  </div>
                </div>

                {/* ── Booking section ── */}
                <div className="mu__section">
                  <h3 className="mu__section-title">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    Booking History
                  </h3>

                  {!selected.bookings ? (
                    <p className="mu__loading-text">Loading bookings…</p>
                  ) : selected.bookings.length === 0 ? (
                    <div className="mu__no-bookings-msg">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      <p>No bookings yet</p>
                    </div>
                  ) : (
                    <>
                      <div className="mu__booking-summary">
                        <div className="mu__booking-summary-item">
                          <span className="mu__booking-summary-val">{selected.bookings.length}</span>
                          <span className="mu__booking-summary-lbl">Total Bookings</span>
                        </div>
                        <div className="mu__booking-summary-item">
                          <span className="mu__booking-summary-val">
                            ₹{fmtPrice(selected.bookings.reduce((s, b) => s + (b.grandTotal || 0), 0))}
                          </span>
                          <span className="mu__booking-summary-lbl">Total Spent</span>
                        </div>
                        <div className="mu__booking-summary-item">
                          <span className="mu__booking-summary-val">
                            {selected.bookings.filter(b => b.status === 'confirmed' || b.status === 'completed').length}
                          </span>
                          <span className="mu__booking-summary-lbl">Confirmed</span>
                        </div>
                      </div>
                      <div className="mu__bookings-list">
                        {selected.bookings.map(b => (
                          <div key={b._id} className="mu__booking-item">
                            <div className="mu__booking-item-left">
                              <span className="mu__booking-dot" style={{ background: BOOKING_STATUS_COLOR[b.status] || '#aaa' }} />
                              <div>
                                <div className="mu__booking-trip">{b.tripTitle}</div>
                                <div className="mu__booking-meta">
                                  {b.bookingId} · {fmtDate(b.travelDate)} · {b.travelers} traveler{b.travelers > 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>
                            <div className="mu__booking-item-right">
                              <span className="mu__booking-amount">₹{fmtPrice(b.grandTotal)}</span>
                              <span className="mu__booking-status" style={{ color: BOOKING_STATUS_COLOR[b.status] || '#aaa' }}>
                                {b.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className="mu__meta">
                  <span>Joined {fmtDateTime(selected.createdAt)}</span>
                  {selected.updatedAt && selected.updatedAt !== selected.createdAt && (
                    <span>Updated {fmtDateTime(selected.updatedAt)}</span>
                  )}
                </div>

              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}